const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api";
export const appBaseUrl = import.meta.env.VITE_APP_BASE_URL;
interface ApiResponse {
  success: boolean;
  data?: unknown;
  message?: string;
  status?: number;
}

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

const request = async (
  url: string,
  options: RequestOptions = {},
): Promise<ApiResponse> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        "accept-language": "en",
        "x-hakuba-platform": "web",
        "x-hakuba-version": "1.0.0",
        ...(options.headers ?? {}),
      },
    });

    const contentType = response.headers.get("content-type") ?? "";
    const isJson = contentType.includes("application/json");
    const body = isJson ? await response.json() : null;

    if (!isJson) {
      return {
        success: false,
        status: response.status,
        message: "Invalid response format",
      };
    }

    // Expect server to already wrap as { success, message, data }
    return {
      ...(body as Record<string, unknown>),
      status: response.status,
    } as ApiResponse;
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "API request failed";
    return { success: false, message };
  }
};

// Basic error handler used by hooks
export const handleError = (error: unknown) => {
  console.error("API Error:", error);

  // Extract meaningful error message
  if (error && typeof error === "object" && "message" in error) {
    throw new Error(String(error.message));
  }

  throw new Error("An unexpected error occurred");
};

export const handleResponse = (response: ApiResponse) => {
  if (!response?.success) {
    return {
      success: false,
      data: null,
      message: response?.message || "Request failed",
    };
  }
  return response?.data ?? response;
};

export const get = (url: string, options: RequestOptions = {}) =>
  request(`${BASE_URL}${url}`, options);

export const post = (
  url: string,
  data?: unknown,
  options: RequestOptions = {},
) => {
  return request(`${BASE_URL}${url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
    ...options,
    body: data === undefined ? undefined : JSON.stringify(data),
  });
};

const sendRequest = (
  method: "PUT" | "DELETE" | "PATCH",
  url: string,
  data?: unknown,
  customOptions: RequestOptions = {},
) => {
  return request(`${BASE_URL}${url}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(customOptions.headers ?? {}),
    },
    ...customOptions,
    body: data === undefined ? undefined : JSON.stringify(data),
  });
};

export const put = (
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions,
) => sendRequest("PUT", url, data, options);

export const del = (
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions,
) => sendRequest("DELETE", url, data, options);

export const patch = (
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions,
) => sendRequest("PATCH", url, data, options);

// keep named export to satisfy imports
